import { HTTPError } from "ky";

import { getMessageForCode } from "./errorCodes";
import { looksLikeUpstreamLeak, sanitizeUpstreamErrorMessage } from "./sanitizeUpstreamErrorMessage";

/**
 * API Error Response Structure.
 *
 * The backend's centralized exception handlers emit a single, consistent shape:
 * {
 *   "code": <status_code | domain_code>,     // numeric HTTP status, or a string
 *                                            // domain code such as "SESSION_EXPIRED"
 *   "message": "<human-readable message>",
 *   "errors": { "<field>": ["<msg>", ...] }, // OPTIONAL — field-level errors
 *                                            // (422 validation + any error with a `field`)
 *   "action": "<suggested next step>",       // OPTIONAL — e.g. for 401 / 429
 *   "retry_after": <seconds>                 // OPTIONAL — 429 rate limiting
 * }
 *
 * `details` (a FastAPI-style array of {loc, msg, type}) is the legacy shape and is
 * still parsed defensively, but the backend no longer emits it.
 */
export type TApiErrorResponse = {
  code: number | string;
  message: string;
  /** Field-level errors keyed by backend field name — the current contract. */
  errors?: Record<string, string[] | string>;
  /** Suggested next step for the user (e.g. "Please sign in again to continue."). */
  action?: string;
  /** Seconds to wait before retrying — present on 429 responses. */
  retry_after?: number;
  /** Structured domain detail (e.g. `{ code: "SESSION_EXPIRED" }`) — legacy/compat. */
  detail?: { code?: string } | string;
  /** @deprecated Legacy FastAPI-style validation array. No longer emitted. */
  details?: {
    loc: (string | number)[];
    msg: string;
    type: string;
  }[];
};

/**
 * Maps backend field names (snake_case) to frontend field names (camelCase)
 * @param backendFieldName - The field name from the backend
 * @returns The corresponding frontend field name
 */
function mapBackendToFrontendField(backendFieldName: string): string {
  const fieldMapping: Record<string, string> = {
    first_name: "firstName",
    last_name: "lastName",
    phone_number: "phone",
    confirm_password: "confirmPassword",
    // Fields that are the same in both
    email: "email",
    password: "password",
  };

  return fieldMapping[backendFieldName] ?? backendFieldName;
}

/**
 * Appends the backend-provided `action` hint to a message so the copy stays
 * actionable (e.g. "Session expired. Please sign in again to continue."),
 * avoiding duplication when the action is already part of the message.
 */
/**
 * Detects messages that are a serialized object/array rather than a sentence
 * (e.g. a backend bug stringifying a dict: "{'code': 'X', 'message': '...'}").
 * Such strings are developer debris, never fit for the UI.
 */
function looksLikeSerializedObject(message: string): boolean {
  const trimmed = message.trim();
  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  );
}

function appendAction(message: string, action?: string): string {
  const base = message.trim();
  const hint = action?.trim();
  if (!hint) return base;
  if (base.toLowerCase().includes(hint.toLowerCase())) return base;
  return `${base} ${hint}`;
}

/**
 * Extracts error response from HTTPError
 * @param error - The error object (HTTPError or other)
 * @returns The API error response structure or null
 */
export async function extractApiError(error: unknown): Promise<TApiErrorResponse | null> {
  if (!(error instanceof HTTPError)) {
    return null;
  }

  try {
    const response = error.response;
    if (!response) {
      return null;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      return null;
    }

    // Clone the response to avoid consuming it
    const clonedResponse = response.clone();
    const json = (await clonedResponse.json().catch(() => null)) as unknown;

    if (json && typeof json === "object" && json !== null && "code" in json && "message" in json) {
      return json as TApiErrorResponse;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extracts user-friendly error message from an error.
 *
 * Resolution order:
 *   1. Offline / network detection (TypeError with no connectivity)
 *   2. Backend error code catalog (detail.code → product-owned copy)
 *   3. Backend message string (from JSON body)
 *   4. Generic safe fallback
 */
export async function extractErrorMessage(error: unknown): Promise<string> {
  // Offline / network failure detection
  if (error instanceof TypeError) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return "You appear to be offline. Please check your internet connection and try again.";
    }
    return "We couldn't reach the server. Please check your connection and try again.";
  }

  // Sanitize 5xx service errors — never surface internal URLs or stack traces.
  // Prefer a trusted backend { code, message } body when it does not look leaky.
  if (error instanceof HTTPError && [502, 503, 504].includes(error.response.status)) {
    const apiError = await extractApiError(error);
    if (apiError?.message && !looksLikeUpstreamLeak(apiError.message)) {
      const nestedCode =
        typeof apiError.detail === "object" ? (apiError.detail?.code ?? null) : null;
      const catalogMessage = getMessageForCode(nestedCode ?? apiError.code);
      if (catalogMessage) return appendAction(catalogMessage, apiError.action);
      if (!looksLikeSerializedObject(apiError.message)) {
        return appendAction(apiError.message, apiError.action);
      }
    }
    return "The service is temporarily unavailable. Please try again later.";
  }

  // Try to extract structured API error from HTTPError body
  const apiError = await extractApiError(error);

  // Check backend error code catalog first (product-owned copy)
  if (apiError) {
    // A string domain code can arrive either at the top level (`code`) or nested
    // under `detail.code`, depending on the endpoint. Try both.
    const nestedCode =
      typeof apiError.detail === "object" ? (apiError.detail?.code ?? null) : null;
    const catalogMessage = getMessageForCode(nestedCode ?? apiError.code);
    if (catalogMessage) return appendAction(catalogMessage, apiError.action);
  }

  if (apiError?.message && !looksLikeSerializedObject(apiError.message)) {
    return sanitizeUpstreamErrorMessage(appendAction(apiError.message, apiError.action));
  }

  // Handle HTTPError responses to surface backend-provided messages
  if (error instanceof HTTPError) {
    try {
      const response = error.response;
      if (response) {
        const contentType = response.headers.get("content-type") ?? "";
        if (contentType.includes("application/json")) {
          const clonedResponse = response.clone();
          const json = (await clonedResponse.json().catch(() => null)) as unknown;
          if (
            json &&
            typeof json === "object" &&
            json !== null &&
            "message" in json &&
            typeof (json as { message: unknown }).message === "string" &&
            !looksLikeSerializedObject((json as { message: string }).message)
          ) {
            return (json as { message: string }).message;
          }
        }
      }
    } catch {
      // Ignore parsing errors
    }
    return "An unexpected error occurred. Please try again.";
  }

  // Generic fallback — never expose raw Error.message to users
  return "An unexpected error occurred. Please try again.";
}

/**
 * Extracts field-specific validation errors from any error response that carries
 * them. Field errors are matched by presence — not by status code — so field-level
 * business errors (e.g. a 400 "Email already registered" tagged with `field=email`)
 * bind to the right input just like 422 schema-validation errors do.
 *
 * Two shapes are supported:
 *   1. `errors: { "<field>": ["<msg>", ...] }`  — the current backend contract.
 *   2. `details: [{ loc, msg, type }]`          — legacy FastAPI shape (fallback).
 *
 * Backend field names (snake_case) are mapped to frontend field names (camelCase).
 * Multiple messages for one field (e.g. several password-policy failures) are
 * joined into a single readable string. The synthetic `unknown` bucket — used by
 * the backend for errors not tied to a field — is skipped so it surfaces globally.
 *
 * @returns Map of frontend field names to messages, or null if there are none.
 */
export async function extractValidationErrors(error: unknown): Promise<Record<string, string> | null> {
  const apiError = await extractApiError(error);
  if (!apiError) {
    return null;
  }

  const validationErrors: Record<string, string> = {};

  // Primary contract: `errors` dict keyed by field name.
  if (apiError.errors && typeof apiError.errors === "object") {
    for (const [backendFieldName, rawMessages] of Object.entries(apiError.errors)) {
      if (backendFieldName === "unknown") continue; // not field-specific → handled globally
      const messages = Array.isArray(rawMessages) ? rawMessages : [rawMessages];
      const message = messages
        .filter(Boolean)
        .map(String)
        .join(" ")
        .trim();
      if (message) {
        validationErrors[mapBackendToFrontendField(backendFieldName)] = message;
      }
    }
  }

  // Legacy fallback: FastAPI-style `details` array.
  if (Object.keys(validationErrors).length === 0 && Array.isArray(apiError.details)) {
    for (const detail of apiError.details) {
      const fieldPath = detail.loc;
      if (fieldPath && fieldPath.length > 0) {
        const backendFieldName = String(fieldPath[fieldPath.length - 1]);
        validationErrors[mapBackendToFrontendField(backendFieldName)] = detail.msg;
      }
    }
  }

  return Object.keys(validationErrors).length > 0 ? validationErrors : null;
}

/**
 * Returns true when the error is a 401 HTTPError carrying the structured
 * `"SESSION_EXPIRED"` domain code emitted by the backend's verification-route
 * dependency. The backend spreads that code to the top level of the body
 * (`{ code: "SESSION_EXPIRED", message, action }`); older shapes nested it under
 * `detail`, so both locations are checked for resilience.
 */
export async function isSessionExpiredCode(error: unknown): Promise<boolean> {
  if (!(error instanceof HTTPError) || error.response?.status !== 401) {
    return false;
  }
  try {
    const cloned = error.response.clone();
    const json = (await cloned.json().catch(() => null)) as unknown;
    if (!json || typeof json !== "object") return false;

    // Current contract: top-level `code`.
    if ((json as { code?: unknown }).code === "SESSION_EXPIRED") return true;

    // Legacy/compat: nested `detail.code` or a bare `detail` string.
    const detail = (json as { detail?: unknown }).detail;
    if (detail && typeof detail === "object" && "code" in detail) {
      return (detail as { code: unknown }).code === "SESSION_EXPIRED";
    }
    return detail === "SESSION_EXPIRED";
  } catch {
    return false;
  }
}

/**
 * Gets the status code from an error
 * @param error - The error object
 * @returns HTTP status code or null
 */
export function getErrorStatusCode(error: unknown): number | null {
  if (error instanceof HTTPError) {
    return error.response?.status ?? null;
  }
  return null;
}

const DUPLICATE_COMPANY_NAME_MESSAGE = "A company with this name already exists.";

/**
 * User-facing message for company creation failures (e.g. duplicate name 409).
 */
export async function getCreateCompanyErrorMessage(error: unknown): Promise<string> {
  const statusCode = getErrorStatusCode(error);
  const message = await extractErrorMessage(error);

  if (statusCode === 409 || /already exists/i.test(message)) {
    return DUPLICATE_COMPANY_NAME_MESSAGE;
  }

  return message || "Failed to create company. Please try again.";
}
